/**
 * Line / Area Chart Renderer
 *
 * Creates an amCharts 5 XYChart with LineSeries from a LineAreaConfig.
 * The area variant fills below the line with optional opacity control.
 */

import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';

import type { LineAreaConfig } from '../../../../types/dashboard';
import { DASHBOARD_TOKENS } from '../tokens';
import { buildAmChartsNumberFormat } from '../formatters';

export function buildLineAreaChart(
  root: am5.Root,
  config: LineAreaConfig,
  data: Record<string, unknown>[],
  isArea: boolean
): am5xy.XYChart {
  const chart = root.container.children.push(
    am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      wheelX: 'none',
      wheelY: 'none',
      layout: root.verticalLayout,
    })
  );

  // Date axis on X
  const xRenderer = am5xy.AxisRendererX.new(root, {
    minGridDistance: 60,
  });
  xRenderer.labels.template.setAll({
    fontSize: DASHBOARD_TOKENS.fontSizeSmall,
    fill: am5.color(DASHBOARD_TOKENS.textSecondary),
  });

  // Detect whether X values are real timestamps or categorical strings (e.g. "08 AM" from hour_of_day).
  // We check the first non-null value in the data for the dateField.
  const firstDateVal = data.find((row) => row[config.dateField] != null)?.[config.dateField];
  const isCategoryAxis =
    firstDateVal == null ||
    (typeof firstDateVal === 'string' && isNaN(new Date(firstDateVal).getTime()));

  let xAxis: am5xy.DateAxis<am5xy.AxisRendererX> | am5xy.CategoryAxis<am5xy.AxisRendererX>;

  if (isCategoryAxis) {
    // Use CategoryAxis for non-date X values (hour_of_day strings, day-of-week labels, etc.)
    xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        renderer: xRenderer,
        categoryField: config.dateField,
        tooltip: am5.Tooltip.new(root, {}),
      })
    ) as unknown as am5xy.CategoryAxis<am5xy.AxisRendererX>;
  } else {
    const dateFormat = config.axes?.x?.dateFormat || 'MMM dd';
    xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: { timeUnit: 'day', count: 1 },
        renderer: xRenderer,
        dateFormats: {
          day: dateFormat,
          week: dateFormat,
          month: 'MMM yyyy',
        },
        periodChangeDateFormats: {
          day: dateFormat,
          week: dateFormat,
          month: 'MMM yyyy',
        },
      })
    ) as unknown as am5xy.DateAxis<am5xy.AxisRendererX>;
  }

  // Value axis on Y
  const yRenderer = am5xy.AxisRendererY.new(root, {});
  yRenderer.labels.template.setAll({
    fontSize: DASHBOARD_TOKENS.fontSizeSmall,
    fill: am5.color(DASHBOARD_TOKENS.textMuted),
  });

  const numberFormat = buildAmChartsNumberFormat(config.axes?.y?.format, config.axes?.y?.currency);

  const yAxis = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      renderer: yRenderer,
      min: config.axes?.y?.min,
      numberFormat,
    })
  );

  // Prepare data: for DateAxis convert strings to timestamps; CategoryAxis uses strings as-is.
  const processedData = isCategoryAxis
    ? data
    : data.map((row) => {
        const dateVal = row[config.dateField];
        return {
          ...row,
          [config.dateField]:
            typeof dateVal === 'string' || typeof dateVal === 'number'
              ? new Date(dateVal).getTime()
              : dateVal,
        };
      });

  // CategoryAxis must have its categories registered BEFORE series data is set,
  // otherwise the series cannot resolve positions on the first render.
  if (isCategoryAxis) {
    (xAxis as am5xy.CategoryAxis<am5xy.AxisRendererX>).data.setAll(processedData);
  }

  config.series.forEach((seriesConfig, idx) => {
    const color =
      seriesConfig.color || DASHBOARD_TOKENS.palette[idx % DASHBOARD_TOKENS.palette.length];
    const shouldFill = isArea || seriesConfig.fill === true;

    const series = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: seriesConfig.name,
        xAxis,
        yAxis,
        valueYField: seriesConfig.valueField,
        // CategoryAxis uses categoryXField; DateAxis uses valueXField
        ...(isCategoryAxis
          ? { categoryXField: config.dateField }
          : { valueXField: config.dateField }),
        stroke: am5.color(color),
        fill: am5.color(color),
        tooltip: am5.Tooltip.new(root, {
          labelText: seriesConfig.tooltip || '{name}: {valueY}',
        }),
      })
    );

    series.strokes.template.setAll({
      strokeWidth: config.visual?.strokeWidth ?? 2,
    });

    if (shouldFill) {
      series.fills.template.setAll({
        visible: true,
        fillOpacity: seriesConfig.fillOpacity ?? 0.15,
      });
    }

    // Add circle bullets so individual data points are visible even with a single value.
    series.bullets.push(() =>
      am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 4,
          fill: am5.color(color),
          stroke: root.interfaceColors.get('background'),
          strokeWidth: 1.5,
        }),
      })
    );

    series.data.setAll(processedData);
    series.appear();
  });

  // Legend
  if (config.visual?.legend !== false && config.series.length > 1) {
    const legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.percent(50),
        x: am5.percent(50),
      })
    );
    legend.labels.template.setAll({
      fill: am5.color(DASHBOARD_TOKENS.textSecondary),
      fontSize: DASHBOARD_TOKENS.fontSizeSmall,
    });
    legend.data.setAll(chart.series.values);
  }

  // Cursor
  if (config.visual?.cursor !== false) {
    chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        behavior: 'none',
        xAxis,
      })
    );
  }

  return chart;
}
